import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router'
import { trimVideo } from 'shared/api'
import DotsLoader from 'shared/components/loaders/dots'
import { CreationToolsContext } from '../../manager'
import { CreationToolSourceFile } from '../../types'
import CreationToolError from '../../types/error'
import CreationToolFileType from '../../types/file-type'
import CreationToolOutputType from '../../types/output-type'
import CreationToolStep from '../../types/steps'
import CreationToolUserRestriction from '../../types/user-restriction'
import getDefaultSourceTitle from '../../utils/get-default-source-title'
import getVideoInfo from '../../utils/get-video-info'
import { ColumnBlock, ContainerBlock, SplitBlock } from '../blocks'
import Breadcrumbs from '../breadcrumbs'
import Navigation from '../navigation'
import { Text, Title } from '../typography'
import VideoPlayer from '../video-player'
import Buttons from './buttons'
import Trimmer from './trimmer'

export const STEP = CreationToolStep.VideoTrim

const CreationToolsVideoTrim = () => {
    const { state } = useLocation()
    const { sources, setSources, outputType, setOutputType, userRestrictionAccess, trackEvent } = useContext(
        CreationToolsContext
    )
    const [startTime, setStartTime] = useState<number>(0)
    const [endTime, setEndTime] = useState<number>(0)
    const [playheadTime, setPlayheadTime] = useState<number>(0)
    const [isProcessing, setIsProcessing] = useState<boolean>(false)
    const [isTrimmed, setIsTrimmed] = useState<boolean>(false)
    const source = sources.find(source => {
        if (state && (state as any).sourceId) {
            return source.id === (state as any).sourceId
        }
        return true
    })

    if (!source) return null

    const duration = endTime - startTime
    const minDuration = CreationToolOutputType.minDuration(outputType)
    const maxDuration = CreationToolOutputType.maxDuration(outputType)
    const invalidDuration = duration < minDuration || duration > maxDuration
    const canUploadVideo = userRestrictionAccess.indexOf(CreationToolUserRestriction.CanUploadVideo) > -1
    const canUploadAsClip = outputType === CreationToolOutputType.Video || (canUploadVideo && source.isUrl)

    const onTimeUpdate = (start: number, end: number, trimmed: boolean) => {
        setStartTime(start)
        setEndTime(end)
        setIsTrimmed(trimmed)
    }

    const onBeforeContinue = async (outputType?: CreationToolOutputType) => {
        if (!source) return

        if (!isTrimmed && source.type !== CreationToolFileType.VIDEO_EMBED) {
            return
        }

        setIsProcessing(true)

        try {
            const startRounded = Math.round(startTime * 10) / 10
            const durationRounded = Math.round((endTime - startTime) * 10) / 10
            const trimmedVideoBlob = await trimVideo(source.source, startRounded, durationRounded)
            const updatedSources: CreationToolSourceFile[] = []

            if (!trimmedVideoBlob || trimmedVideoBlob.size < 100) {
                throw new Error('An error occurred trimming video')
            }

            // change output type, if set
            if (outputType) {
                setOutputType(outputType)
            }

            for (let i = 0; i < sources.length; i++) {
                let sourceFile = sources[i]

                if (sourceFile.id === source.id) {
                    const filename = getDefaultSourceTitle(sourceFile)
                    const timmedFileUrl = URL.createObjectURL(trimmedVideoBlob)
                    const { width, height } = await getVideoInfo(timmedFileUrl)

                    URL.revokeObjectURL(timmedFileUrl)

                    sourceFile = {
                        ...sourceFile,
                        width,
                        height,
                        source: new File([trimmedVideoBlob], filename, {
                            type: trimmedVideoBlob.type,
                            lastModified: Date.now(),
                        }),
                        duration: durationRounded,
                        type: CreationToolFileType.VIDEO,
                        isUrl: false,
                    }
                }

                updatedSources.push(sourceFile)
            }
            setSources(updatedSources)
        } catch (err) {
            setIsProcessing(false)
            trackEvent(
                undefined,
                CreationToolError.gaAction(
                    CreationToolError.VideoTrimError,
                    outputType || CreationToolOutputType.Video
                ),
                err.message || 'No error message'
            )
            throw new Error(CreationToolError.description(CreationToolError.VideoTrimError))
        }
    }

    return (
        <>
            <ContainerBlock style={isProcessing ? { pointerEvents: 'none', opacity: 0 } : {}}>
                <SplitBlock>
                    <ColumnBlock>
                        <div>
                            <Navigation />
                            {!isProcessing && (
                                <VideoPlayer
                                    {...{
                                        source,
                                        startTime,
                                        endTime,
                                        width: 460,
                                        hideButtons: !canUploadAsClip,
                                        onTimeUpdate: setPlayheadTime,
                                    }}
                                />
                            )}
                        </div>
                        <Breadcrumbs />
                    </ColumnBlock>
                    <ColumnBlock>
                        <div>
                            <Title as="h1">{CreationToolStep.title(STEP)}</Title>
                            <Text>{CreationToolStep.description(STEP)}</Text>
                            <Trimmer
                                {...{
                                    source,
                                    invalidDuration,
                                    playheadTime,
                                    onTimeUpdate,
                                }}
                            />
                        </div>
                        <Buttons
                            {...{
                                source,
                                disabled: invalidDuration,
                                onBeforeContinue,
                            }}
                        />
                    </ColumnBlock>
                </SplitBlock>
            </ContainerBlock>
            {isProcessing && <DotsLoader isAbsolute />}
        </>
    )
}

export default CreationToolsVideoTrim
